<template>
  <div class="site-page site-page--eighty nav-content-inset">
    <div class="site-page__inner simple-course">
      <div class="page-head">
        <h1 class="site-page__title">{{ isEdit ? '改简易课程' : '新建简易课程' }}</h1>
        <router-link class="back-link" to="/creator/courses">← 返回我创建的课程</router-link>
      </div>
      <p class="hint">
        极简模式：B 站链接需同时填标题（数量一致）；系统会自动拉取各节视频封面作为知识点头图。未上传课程封面时，用第一节的 B 站封面作课程头图。MP3 用文件名作课节标题；ZIP 用压缩包名作课程标题。
        删除时整门课一次清空。
      </p>

      <el-form label-position="top" class="simple-form" @submit.prevent>
        <el-form-item v-if="mode !== 'zip'" label="课程名称" required>
          <el-input v-model="form.name" maxlength="80" placeholder="例如：MySQL 入门音频课" />
        </el-form-item>
        <el-form-item v-else label="课程名称">
          <el-input :model-value="zipCourseName" disabled placeholder="选择 zip 后自动使用文件名" />
          <p class="field-hint">ZIP 建课：压缩包文件名（去掉 .zip）即为课程标题</p>
        </el-form-item>
        <el-form-item label="简介">
          <el-input v-model="form.description" type="textarea" :rows="2" maxlength="200" />
        </el-form-item>
        <el-form-item label="内容标签">
          <ContentTagSelect v-model="form.tagIds" scope="course" />
        </el-form-item>
        <el-form-item label="定价（元）">
          <el-input-number v-model="form.feeYuan" :min="0" :step="1" :precision="2" />
          <span class="fee-tip">0 表示免费</span>
        </el-form-item>
        <el-form-item label="课程封面">
          <div class="banner-upload-wrap">
            <el-upload
              class="banner-uploader"
              action="/api/v1/upload"
              :show-file-list="false"
              :headers="uploadHeaders"
              :data="uploadData"
              accept="image/png,image/jpeg,image/webp"
              :before-upload="beforeBannerUpload"
              :on-success="handleBannerSuccess"
            >
              <img
                v-if="form.banner && !bannerBroken"
                :src="form.banner"
                class="banner-preview"
                alt="课程封面"
                @error="bannerBroken = true"
              />
              <el-icon v-else class="banner-uploader-icon"><Plus /></el-icon>
            </el-upload>
            <el-button
              v-if="form.banner"
              link
              type="danger"
              class="btn-clear-banner"
              @click="clearBanner"
            >清除封面</el-button>
          </div>
        </el-form-item>

        <el-tabs v-model="mode" :before-leave="beforeModeLeave">
          <el-tab-pane label="B站视频链接" name="bilibili">
            <p class="tab-hint">标题与视频各占一行，一一对应，数量必须相同</p>
            <el-form-item label="课节标题（每行一个）" required>
              <el-input
                v-model="bilibiliTitlesText"
                type="textarea"
                :rows="8"
                placeholder="第1节：开场介绍&#10;第2节：基础语法&#10;第3节：实战演练"
              />
            </el-form-item>
            <el-form-item label="B站视频链接（每行一个）" required>
              <el-input
                v-model="bilibiliUrlsText"
                type="textarea"
                :rows="8"
                placeholder="https://www.bilibili.com/video/BVxxxxxxxx&#10;https://www.bilibili.com/video/BVyyyyyyyy"
              />
            </el-form-item>
            <p v-if="bilibiliCountHint" class="count-hint" :class="{ 'count-hint--err': bilibiliCountMismatch }">
              {{ bilibiliCountHint }}
            </p>
          </el-tab-pane>
          <el-tab-pane label="上传 MP3" name="audio">
            <p class="tab-hint">可多选 mp3；课节标题自动取自文件名（去掉扩展名）</p>
            <el-upload
              drag
              multiple
              :auto-upload="false"
              :file-list="audioFileList"
              accept=".mp3,.m4a,.wav,.flac,.aac,.ogg"
              :on-change="onAudioChange"
              :on-remove="onAudioRemove"
            >
              <div class="el-upload__text">拖拽文件到此处，或 <em>点击选择</em></div>
            </el-upload>
            <ul v-if="mp3PreviewTitles.length" class="item-preview">
              <li v-for="(t, i) in mp3PreviewTitles" :key="i">{{ i + 1 }}. {{ t }}</li>
            </ul>
          </el-tab-pane>
          <el-tab-pane label="ZIP 一键建课" name="zip" :disabled="isEdit">
            <p class="tab-hint">上传一个 zip：课程标题 = zip 文件名；课节标题 = 包内各音频文件名</p>
            <el-upload
              drag
              :auto-upload="false"
              :limit="1"
              accept=".zip"
              :on-change="onZipChange"
              :on-remove="onZipRemove"
            >
              <div class="el-upload__text">选择 zip 压缩包</div>
            </el-upload>
          </el-tab-pane>
        </el-tabs>

        <div class="actions">
          <el-button type="primary" :loading="saving" @click="submit">
            {{ isEdit ? '整课替换保存' : '创建课程' }}
          </el-button>
          <el-button v-if="isEdit" type="danger" plain :loading="deleting" @click="removeCourse">
            删除整门课
          </el-button>
          <el-button @click="$router.push('/creator/courses')">取消</el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import {
  adminUploadHeaders,
  adminUploadFormData,
  uploadResponseToUrl,
  validatePhotoUpload,
  photoPathToUrl
} from '@/utils/photoUpload'

import ContentTagSelect from '@/components/ContentTagSelect.vue'

function authHeaders() {
  return { Authorization: 'Bearer ' + (localStorage.getItem('jwt_token') || '') }
}

function parseLines(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function stripExt(filename) {
  const base = String(filename || '').split(/[/\\]/).pop() || ''
  return base.replace(/\.[^.]+$/, '')
}

export default {
  name: 'CreatorSimpleCourseView',
  components: { Plus, ContentTagSelect },
  data() {
    return {
      kcId: null,
      mode: 'bilibili',
      form: {
        name: '',
        description: '',
        feeYuan: 0,
        banner: '',
        tagIds: []
      },
      bannerBroken: false,
      bilibiliTitlesText: '',
      bilibiliUrlsText: '',
      audioFileList: [],
      audioRawFiles: [],
      audioItems: [],
      zipFile: null,
      saving: false,
      deleting: false,
      loading: false
    }
  },
  computed: {
    isEdit() {
      return !!this.kcId
    },
    uploadHeaders() {
      return adminUploadHeaders()
    },
    uploadData() {
      return adminUploadFormData('courses')
    },
    bilibiliTitles() {
      return parseLines(this.bilibiliTitlesText)
    },
    bilibiliUrls() {
      return parseLines(this.bilibiliUrlsText)
    },
    bilibiliCountMismatch() {
      const tc = this.bilibiliTitles.length
      const uc = this.bilibiliUrls.length
      return tc > 0 && uc > 0 && tc !== uc
    },
    bilibiliCountHint() {
      const tc = this.bilibiliTitles.length
      const uc = this.bilibiliUrls.length
      if (!tc && !uc) return ''
      if (tc === uc) return `已对齐：${tc} 个标题 · ${uc} 个视频`
      return `数量不一致：${tc} 个标题 · ${uc} 个视频（须相同）`
    },
    zipCourseName() {
      if (!this.zipFile) return ''
      return stripExt(this.zipFile.name)
    },
    mp3PreviewTitles() {
      if (this.audioRawFiles.length) {
        return this.audioRawFiles.map((f) => stripExt(f.name))
      }
      return (this.audioItems || []).map((it) => it.title || it.name).filter(Boolean)
    }
  },
  created() {
    const id = this.$route.query.kcId
    if (id) {
      this.kcId = Number(id)
      this.loadCourse()
    }
  },
  methods: {
    beforeModeLeave() {
      return true
    },
    beforeBannerUpload(file) {
      return validatePhotoUpload(file, (msg) => ElMessage.error(msg))
    },
    handleBannerSuccess(response) {
      if (response.code === 200) {
        this.form.banner = uploadResponseToUrl(response)
        this.bannerBroken = false
        ElMessage.success('封面已上传')
      } else {
        ElMessage.error(response.message || '封面上传失败')
      }
    },
    clearBanner() {
      this.form.banner = ''
      this.bannerBroken = false
    },
    async loadCourse() {
      this.loading = true
      try {
        const res = await fetch('/api/v1/creator/knowledge/courses/simple/get', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ kcId: this.kcId })
        })
        const data = await res.json()
        if (data.code !== 200 || !data.data) {
          ElMessage.error(data.message || '加载失败')
          return
        }
        const d = data.data
        this.form.name = d.name || ''
        this.form.description = d.description || ''
        this.form.feeYuan = (d.fee || 0) / 100
        this.form.banner = photoPathToUrl(d.banner || '')
        this.form.tagIds = (d.tagIds || []).map((id) => Number(id)).filter((id) => id > 0)
        this.bannerBroken = false
        this.mode = d.mode === 'bilibili' ? 'bilibili' : 'audio'
        const items = d.items || []
        if (this.mode === 'bilibili') {
          this.bilibiliTitlesText = items.map((it) => it.title || '').join('\n')
          this.bilibiliUrlsText = items.map((it) => it.url || '').filter(Boolean).join('\n')
        } else {
          this.audioItems = items.map((it) => ({
            title: it.title,
            name: it.title,
            url: it.url
          }))
        }
      } catch (e) {
        ElMessage.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    onAudioChange(file, fileList) {
      this.audioFileList = fileList
      this.audioRawFiles = fileList.map((f) => f.raw).filter(Boolean)
      if (this.audioRawFiles.length) {
        this.audioItems = []
      }
    },
    onAudioRemove(file, fileList) {
      this.audioFileList = fileList
      this.audioRawFiles = fileList.map((f) => f.raw).filter(Boolean)
    },
    onZipChange(file) {
      this.zipFile = file.raw || null
      if (this.zipFile) {
        this.form.name = stripExt(this.zipFile.name)
      }
    },
    onZipRemove() {
      this.zipFile = null
    },
    feeFen() {
      return Math.round((Number(this.form.feeYuan) || 0) * 100)
    },
    async uploadAudioFiles() {
      if (!this.audioRawFiles.length) {
        if (this.audioItems.length) {
          return this.audioItems.map((it) => ({
            title: it.title || it.name,
            url: it.url
          }))
        }
        throw new Error('请先选择音频文件')
      }
      const fd = new FormData()
      this.audioRawFiles.forEach((f) => fd.append('files', f))
      const res = await fetch('/api/v1/creator/knowledge/media/upload', {
        method: 'POST',
        headers: authHeaders(),
        body: fd
      })
      const data = await res.json()
      if (data.code !== 200 || !data.data?.items?.length) {
        throw new Error(data.message || '上传失败')
      }
      const items = data.data.items.map((it, i) => {
        const raw = this.audioRawFiles[i]
        const title = raw ? stripExt(raw.name) : (it.name || it.title)
        return { title, url: it.url }
      })
      this.audioItems = items.map((it) => ({ ...it, name: it.title }))
      return items
    },
    async submit() {
      if (this.mode !== 'zip' && !this.form.name.trim()) {
        ElMessage.warning('请填写课程名称')
        return
      }
      this.saving = true
      try {
        if (this.mode === 'zip' && !this.isEdit) {
          if (!this.zipFile) {
            ElMessage.warning('请选择 zip')
            return
          }
          if (!this.zipCourseName) {
            ElMessage.warning('zip 文件名无效')
            return
          }
          const fd = new FormData()
          fd.append('name', this.zipCourseName)
          fd.append('description', this.form.description || '')
          fd.append('fee', String(this.feeFen()))
          fd.append('category', '简易课程')
          fd.append('banner', this.form.banner || '')
          fd.append('tagIds', JSON.stringify(this.form.tagIds || []))
          fd.append('file', this.zipFile)
          const res = await fetch('/api/v1/creator/knowledge/courses/simple/from-zip', {
            method: 'POST',
            headers: authHeaders(),
            body: fd
          })
          const data = await res.json()
          if (data.code !== 200) {
            ElMessage.error(data.message || '创建失败')
            return
          }
          ElMessage.success('课程已创建，待管理员审核通过后才会出现在公开列表')
          this.$router.push('/creator/courses')
          return
        }

        let items = []
        let apiMode = this.mode
        if (this.mode === 'bilibili') {
          const titles = this.bilibiliTitles
          const urls = this.bilibiliUrls
          if (!urls.length) {
            ElMessage.warning('请填写至少一条 B 站链接')
            return
          }
          if (!titles.length) {
            ElMessage.warning('请填写课节标题')
            return
          }
          if (titles.length !== urls.length) {
            ElMessage.warning(`标题数量（${titles.length}）与视频数量（${urls.length}）须一致`)
            return
          }
          items = titles.map((title, i) => ({ title, url: urls[i] }))
          apiMode = 'bilibili'
        } else {
          items = await this.uploadAudioFiles()
          apiMode = 'audio'
        }

        const body = {
          name: this.form.name.trim(),
          description: this.form.description || '',
          category: '简易课程',
          fee: this.feeFen(),
          level: 1,
          banner: this.form.banner || '',
          tagIds: this.form.tagIds || [],
          mode: apiMode,
          items
        }
        const url = this.isEdit
          ? '/api/v1/creator/knowledge/courses/simple/replace'
          : '/api/v1/creator/knowledge/courses/simple/create'
        if (this.isEdit) body.kcId = this.kcId

        const res = await fetch(url, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (data.code !== 200) {
          ElMessage.error(data.message || '保存失败')
          return
        }
        ElMessage.success(this.isEdit ? '已整课替换，待重新审核' : '课程已创建，待管理员审核通过后才会出现在公开列表')
        this.$router.push('/creator/courses')
      } catch (e) {
        ElMessage.error(e.message || '操作失败')
      } finally {
        this.saving = false
      }
    },
    async removeCourse() {
      try {
        await ElMessageBox.confirm(
          '将删除整门课程及其全部课时/资源，不可恢复。确定吗？',
          '删除整门课',
          { type: 'warning', confirmButtonText: '全部删除', cancelButtonText: '取消' }
        )
      } catch {
        return
      }
      this.deleting = true
      try {
        const res = await fetch('/api/v1/knowledge/courses/delete', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ kcId: this.kcId })
        })
        const data = await res.json()
        if (data.code !== 200) {
          ElMessage.error(data.message || '删除失败')
          return
        }
        ElMessage.success('已删除')
        this.$router.push('/creator/courses')
      } catch (e) {
        ElMessage.error('删除失败')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.simple-course { max-width: 720px; }
.page-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.back-link { color: #409eff; text-decoration: none; }
.hint { color: #666; margin: 8px 0 20px; line-height: 1.5; }
.tab-hint { color: #909399; font-size: 13px; margin: 0 0 10px; }
.field-hint { margin: 6px 0 0; color: #909399; font-size: 12px; }
.fee-tip { margin-left: 10px; color: #909399; font-size: 13px; }
.actions { margin-top: 24px; display: flex; flex-wrap: wrap; gap: 10px; }
.item-preview {
  margin: 12px 0 0;
  padding-left: 18px;
  color: #606266;
  font-size: 13px;
}
.count-hint {
  margin: 0 0 8px;
  font-size: 13px;
  color: #67c23a;
}
.count-hint--err { color: #f56c6c; }
.banner-upload-wrap {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.banner-uploader {
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  width: 160px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}
.banner-uploader:hover { border-color: #409eff; }
.banner-uploader-icon { font-size: 28px; color: #8c939d; }
.banner-preview {
  width: 160px;
  height: 90px;
  object-fit: cover;
  display: block;
}
.btn-clear-banner { padding: 0; }
</style>
